import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import {
  FormAutocomplete,
  FormCheckbox,
  FormContext,
  FormCustomField,
  FormPanel,
  FormSelect,
  FormTextField,
  SnackbarContext,
  SnackbarVariant,
  abortableFetch,
  useEventCallback,
  useFormSelector,
  useStaticListSource,
} from '@eas/common-web';
import { useContactTypes } from '@modules/dict-contact-type/dict-contact-type-api';
import { useCountries } from '@modules/dict-country/dict-country-api';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { CityField } from '@components/form/address/city-field';
import { ZipCodeField } from '@components/form/address/zip-code-field';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { EvidenceApiUrl } from '@enums';

export const useStyles = makeStyles(() => ({
  actionButtonGroup: {
    width: 100,
  },
  actionButton: {
    width: 100,
    height: 22,
    padding: '0 10px',
    marginLeft: 3,
    fontSize: '12px',
    fontWeight: 600,
    flexShrink: 0,
  },
}));

export function ContactDialog() {
  const { dataBoxTypes } = useContext(StaticDataContext);
  const { getFieldValues, setFieldValue } = useContext(FormContext);

  const dataBoxTypeSource = useStaticListSource(dataBoxTypes);
  const contactTypes = useContactTypes();

  const handleDataBoxChange = useEventCallback(() => {
    const { dataBox } = getFieldValues();

    if (!dataBox?.type && !dataBox?.identifier) {
      setFieldValue('dataBox', undefined);
    }
  });

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__SUBJECTS__DETAIL__PANEL_TITLE__CONTACT"
            defaultMessage="Kontakt"
          />
        }
        expandable={false}
      >
        <FormSelect
          name="type"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__TYPE"
              defaultMessage="Typ kontaktu"
            />
          }
          source={contactTypes}
          labelMapper={autocompleteLabelMapper}
          required
        />
        <FormTextField
          name="titleBefore"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__TITLE_BEFORE"
              defaultMessage="Titul před"
            />
          }
        />
        <FormTextField
          name="firstName"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__FIRST_NAME"
              defaultMessage="Jméno"
            />
          }
        />
        <FormTextField
          name="lastName"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__LAST_NAME"
              defaultMessage="Příjmení"
            />
          }
        />
        <FormTextField
          name="titleAfter"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__TITLE_AFTER"
              defaultMessage="Titul za"
            />
          }
        />
        <FormTextField
          name="department"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__DEPARTMENT"
              defaultMessage="Útvar"
            />
          }
        />
        <FormTextField
          name="email"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__EMAIL"
              defaultMessage="E-mail"
            />
          }
        />
        <FormSelect
          name="dataBox.type"
          source={dataBoxTypeSource}
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__DATA_BOX_TYPE"
              defaultMessage="Typ datové schránky"
            />
          }
          notifyChange={handleDataBoxChange}
          valueIsId
        />
        <FormTextField
          name="dataBox.identifier"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__DATA_BOX"
              defaultMessage="Číslo datové schránky"
            />
          }
          notifyChange={handleDataBoxChange}
        />
        <FormCheckbox
          name="acceptsLetterDataBoxMessage"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__ACCEPTS_LETTER_DATA_BOX_MESSAGES"
              defaultMessage="Přijímá poštovní datové zprávy"
            />
          }
        />
        <FormTextField
          name="phone"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__PHONE"
              defaultMessage="Telefon"
            />
          }
        />
        <FormTextField
          name="otherIdentification"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__OTHER_IDENTIFICATION"
              defaultMessage="Jiná identifikace"
            />
          }
        />
        <FormTextField
          name="function"
          label={
            <FormattedMessage
              id="ES__SUBJECTS__DETAIL__FIELD_LABEL__FUNCTION"
              defaultMessage="Funkce"
            />
          }
        />
      </FormPanel>
      <AddressField prefix="address" />
      {/* <div style={{ marginTop: 16, padding: 8, borderTop: '1px solid #ccc' }}>
        <Button
          variant="outlined"
          onClick={() => {
            validateForm();
          }}
          style={{ width: 'auto' }}
        >
          Debug Validace
        </Button>
      </div> */}
    </>
  );
}

function getAddress(data: {
  cityName: string;
  streetName: string;
  descriptionNumber: string;
  orientationNumber: string;
  zipCode: string;
}) {
  return abortableFetch(`${EvidenceApiUrl.SUBJECTS}/contact/address/search`, {
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    method: 'POST',
    body: JSON.stringify(data),
  });
}

const AddressField = ({ prefix }: { prefix: string }) => {
  const countries = useCountries();
  const classes = useStyles();

  const { showSnackbar } = useContext(SnackbarContext);
  const { setFieldValue } = useContext(FormContext);

  const { city, street, descriptionNumber, orientationNumber, zipCode } =
    useFormSelector((values: any) => ({
      city: values?.[prefix]?.city,
      street: values?.[prefix]?.street,
      descriptionNumber: values?.[prefix]?.descriptiveNumber,
      orientationNumber: values?.[prefix]?.orientationNumber,
      zipCode: values?.[prefix]?.zipCode,
    }));

  const [isSeaerching, setIsSearching] = useState(false);
  const handleSearch = useEventCallback(async () => {
    setIsSearching(true);

    let address = null;

    try {
      address = await getAddress({
        cityName: city,
        streetName: street,
        descriptionNumber: descriptionNumber,
        orientationNumber: orientationNumber,
        zipCode: zipCode?.code,
      }).json();
    } catch (e: any) {
      showSnackbar(
        'Adresní místo nebylo nalezeno: ' + e?.message || 'Neznámá chyba',
        SnackbarVariant.ERROR
      );
      setIsSearching(false);

      console.log(e);
      return;
    }

    showSnackbar(
      'Adresní místo bylo úspěšně nalezeno',
      SnackbarVariant.SUCCESS
    );

    if (address.cityName) {
      setFieldValue(`${prefix}.city`, address.cityName);
    }

    if (address.descriptionNumber) {
      setFieldValue(`${prefix}.descriptiveNumber`, address.descriptionNumber);
    }

    if (address.locationCode) {
      setFieldValue(`${prefix}.locationCode`, address.locationCode);
    }

    if (address.orientationNumber) {
      setFieldValue(`${prefix}.orientationNumber`, address.orientationNumber);
    }

    if (address.streetName) {
      setFieldValue(`${prefix}.street`, address.streetName);
    }

    if (address.zipCode) {
      setFieldValue(`${prefix}.zipCode`, address.zipCode);
    }

    setIsSearching(false);
  });

  return (
    <FormPanel
      label={
        <FormattedMessage
          id="ES__SUBJECTS__DETAIL__PANEL_TITLE__ADDRESS"
          defaultMessage="Adresa"
        />
      }
      expandable={false}
    >
      <FormTextField
        name={`${prefix}.street`}
        label={
          <FormattedMessage
            id="ES__SUBJECTS__DETAIL__FIELD_LABEL__STREET"
            defaultMessage="Ulice"
          />
        }
      />
      <FormTextField
        name={`${prefix}.descriptiveNumber`}
        label={
          <FormattedMessage
            id="ES__SUBJECTS__DETAIL__FIELD_LABEL__DESCRIPTIVE_NUMBER"
            defaultMessage="Číslo popisné"
          />
        }
      />
      <FormTextField
        name={`${prefix}.orientationNumber`}
        label={
          <FormattedMessage
            id="ES__SUBJECTS__DETAIL__FIELD_LABEL__ORIENTATION_NUMBER"
            defaultMessage="Číslo orientační"
          />
        }
      />
      <ZipCodeField prefix={prefix} loadDetail />
      <CityField prefix={prefix} />
      <FormAutocomplete
        name={`${prefix}.country`}
        source={countries}
        label={
          <FormattedMessage
            id="ES__SUBJECTS__DETAIL__FIELD_LABEL__COUNTRY"
            defaultMessage="Stát"
          />
        }
        labelMapper={autocompleteLabelMapper}
      />

      <FormCustomField
        name={`${prefix}.locationCodeWrapper`}
        label={
          <FormattedMessage
            id="ES__SUBJECTS__DETAIL__FIELD_LABEL__LOCATION_CODE"
            defaultMessage="Kód adresního místa"
          />
        }
      >
        <FormTextField
          name={`${prefix}.locationCode`}
          labelOptions={{
            hide: true,
          }}
          layoutOptions={{
            noSpacing: true,
            noUnderline: true,
          }}
        />
        <Button
          onClick={handleSearch}
          variant="text"
          startIcon={<SearchIcon />}
          className={classes.actionButton}
          disabled={isSeaerching}
        >
          Hledat
        </Button>
      </FormCustomField>
    </FormPanel>
  );
};
